import { Button } from "@/atoms-react/button/Button.react";
import { ButtonConfirm } from "@/atoms-react/button/ButtonConfirm.react";
import { Modal, ModalContent } from "@/atoms-react/modal/index.react";
import { WebAuthnRegisterApi } from "@/features/auth/api-client/webauthn-register-api-client";
import { LimitCredentialInterface } from "@/features/auth/types";
import { Themes } from "@/types/theme.ts";
import { SolidAuthnModal } from "@/views/identity/components/security/solid-authn-modal.react";
import { ShieldCheckIcon, ShieldExclamationIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { UpdateWebauthnDevice } from "./UpdateWebauthnDevice.react";
type TrDevice = {
  device: LimitCredentialInterface;
  onComplete: () => Promise<void>;
};
export const TrDevice = ({ onComplete, device }: TrDevice) => {
  const { t } = useTranslation(["pass"]);
  const [loading, setLoading] = useState(false);
  const [changeStatusAction, setChangeStatusAction] = useState(false);
  const [editDeviceName, setEditDeviceName] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const statusAction = async () => {
    try {
      const res = await WebAuthnRegisterApi.activateDeactivateCredential(device.id, !device.active);
      if (res) {
        await onComplete();
      }
    } catch (_) {
      toast.error(t("olkypass.securityPage.webauthnSection.devices.notifications.update.error"));
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await onComplete();
      setEditDeviceName(false);
    } catch (_) {
      toast.error(t("olkypass.securityPage.webauthnSection.devices.notifications.update.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {changeStatusAction && (
        <SolidAuthnModal
          action="activate_deactivate_webauthn_device"
          onClose={() => setChangeStatusAction(false)}
          onValidated={async () => {
            setChangeStatusAction(false);
            setLoading(true);
            await statusAction();
            setLoading(false);
          }}
        />
      )}
      <Modal
        className="w-full min-w-lg max-w-lg items-center justify-center sm:w-full"
        open={editDeviceName}
        onClose={() => setEditDeviceName(false)}
      >
        <ModalContent
          title={t("olkypass.securityPage.webauthnSection.devices.table.actions.update")}
        >
          <UpdateWebauthnDevice device={device} onComplete={handleComplete} />
        </ModalContent>
      </Modal>
      <tr className="border-b bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-600">
        <th
          scope="row"
          className={
            device.active
              ? "whitespace-nowrap px-6 py-4 font-medium text-gray-900 dark:text-white"
              : "whitespace-nowrap px-6 py-4 font-medium text-gray-900 line-through dark:text-white"
          }
        >
          {device.device_name}
        </th>
        <td className="px-6 py-4">{formatDate(device.created_at)}</td>
        <td className="px-6 py-4">{device.domain_name}</td>
        <td className="px-6 py-4">
          {device.active ? (
            <ShieldCheckIcon className="h-5 w-5 text-green-500" />
          ) : (
            <ShieldExclamationIcon className="h-5 w-5 text-yellow-500" />
          )}
        </td>
        <td className="flex flex-row gap-x-2 px-6 py-4 text-right">
          <Button
            className="min-w-60 hover:underline"
            theme={Themes.white}
            onClick={() => setEditDeviceName(true)}
          >
            {t("olkypass.securityPage.webauthnSection.devices.table.actions.update")}
          </Button>
          <ButtonConfirm
            confirmTitle={t("olkypass.securityPage.webauthnSection.devices.confirm_dialog.header")}
            confirmMessage={t("olkypass.securityPage.webauthnSection.devices.confirm_dialog.body")}
            confirmButtonText={t("olkypass.general.confirm")}
            cancelButtonText={t("olkypass.general.cancel")}
            confirmButtonTheme={Themes.white}
            loading={loading}
            onClick={() => {
              setChangeStatusAction(true);
            }}
          >
            {t("olkypass.securityPage.webauthnSection.devices.table.actions.remove")}
          </ButtonConfirm>
        </td>
      </tr>
    </>
  );
};
